import { Container, Divider } from "@material-ui/core";
import {
  Block,
  CheckCircleOutline,
  EditOutlined,
  VisibilityOutlined,
} from "@material-ui/icons";
import { Grid } from "@mui/material";
import axios from "axios";
import {
  ButtonGroupTop,
  CustomTable,
  ModalSuccess,
  ModalWarning,
  Page,
  PrimaryIconButton,
  SearchTextInput,
  StatusChip,
} from "components";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import ModalEmailNotification from "./ModalEmailNotification";

export const initialEmailNotifications = [
  {
    id: 1,
    event_key: "dsrf_success",
    event_name: "DSRF Process Success",
    type: "DSRF Process Success",
    subject: "DSRF Process Completed Successfully",
    to_emails: ["zidane@gmail.com", "b@mail.com"],
    cc_emails: ["admin-dsrf@idas.com"],
    recipient: "zidane@gmail.com, b@mail.com",
    recipients: ["zidane@gmail.com", "b@mail.com"],
    cc: "admin-dsrf@idas.com",
    ccList: ["admin-dsrf@idas.com"],
    total_sent: 21,
    is_active: true,
    status: "Active",
    content:
      "<p>Your DSRF upload and processing has completed successfully. You can now review the processed files and generated reports in the dashboard.</p>",
  },
  {
    id: 2,
    event_key: "dsrf_failed",
    event_name: "DSRF Process Failed",
    type: "DSRF Process Failed",
    subject: "DSRF Processing Failed",
    to_emails: ["support@idas.com", "alert@idas.com"],
    cc_emails: ["tech-lead@idas.com"],
    recipient: "support@idas.com, alert@idas.com",
    recipients: ["support@idas.com", "alert@idas.com"],
    cc: "tech-lead@idas.com",
    ccList: ["tech-lead@idas.com"],
    total_sent: 14,
    is_active: true,
    status: "Active",
    content:
      "<p>An error occurred while processing your DSRF file. Please check the error logs and re-upload the file or contact support if the issue persists.</p>",
  },
  {
    id: 3,
    event_key: "potential_claim_success",
    event_name: "Potential Claim Process Success",
    type: "Potential Claim Process Success",
    subject: "Potential Claim Match Found",
    to_emails: ["claim-team@idas.com", "admin@idas.com"],
    cc_emails: ["manager@idas.com"],
    recipient: "claim-team@idas.com, admin@idas.com",
    recipients: ["claim-team@idas.com", "admin@idas.com"],
    cc: "manager@idas.com",
    ccList: ["manager@idas.com"],
    total_sent: 15,
    is_active: true,
    status: "Active",
    content:
      "<p>New potential claim matches have been identified in the system. Please verify and confirm ownership within the claim management portal.</p>",
  },
  {
    id: 4,
    event_key: "potential_claim_failed",
    event_name: "Potential Claim Process Failed",
    type: "Potential Claim Process Failed",
    subject: "Potential Claim Matching Error",
    to_emails: ["devops@idas.com", "error-logger@idas.com"],
    cc_emails: ["oncall@idas.com"],
    recipient: "devops@idas.com, error-logger@idas.com",
    recipients: ["devops@idas.com", "error-logger@idas.com"],
    cc: "oncall@idas.com",
    ccList: ["oncall@idas.com"],
    total_sent: 12,
    is_active: false,
    status: "Inactive",
    content:
      "<p>Failed to execute potential claim matching algorithm. The system administrator has been alerted and will investigate the issue shortly.</p>",
  },
];

function EmailNotification() {
  const classes = globalStyles();
  const history = useHistory();
  const location = useLocation();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const urlParams = new URLSearchParams(location.search);
  const paramsSearch = urlParams.get("search");
  const paramsPage = urlParams.get("page");
  const paramsSize = urlParams.get("per_page");

  const [loadingPage, setLoadingPage] = useState(false);
  const [dataTable, setDataTable] = useState(initialEmailNotifications);
  const [modalState, setModalState] = useState({
    open: false,
    mode: "detail",
    data: null,
  });
  const [queryParams, setQueryParams] = useState({
    page: Number(paramsPage) || 1,
    per_page: Number(paramsSize) || 10,
    search: paramsSearch || "",
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);

  const handleChangePageParams = (value, key) => {
    urlParams.set(key, value);
    history.replace({
      pathname: location.pathname,
      search: urlParams.toString(),
    });
  };

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
    handleChangePageParams(value, key);
    if (key !== "page") {
      handleChangePageParams(1, "page");
    }
  };

  const handleChangePage = (_, page) => handleChangeQueryParams(page, "page");
  const handleChangePageSize = e =>
    handleChangeQueryParams(e?.target?.value, "per_page");
  const handleSearch = e => handleChangeQueryParams(e?.target?.value, "search");

  const handleView = item => {
    setModalState({ open: true, mode: "detail", data: item });
  };

  const handleEdit = item => {
    setModalState({ open: true, mode: "edit", data: item });
  };

  const handleSaveEdit = updatedItem => {
    setDataTable(prev =>
      prev.map(row => (row.id === updatedItem.id ? { ...row, ...updatedItem } : row))
    );
    getDataTable();
  };

  const handleToggleStatus = item => {
    const isActive =
      typeof item?.is_active === "boolean"
        ? item.is_active
        : item?.status?.toLowerCase() === "active";
    const actionText = isActive ? "inactive" : "active";
    const dialogTitle = isActive
      ? "Inactive Email Notification"
      : "Active Email Notification";
    const displayName = item?.event_name || item?.type || "this document";
    const dialogMessage = `Are you sure you want to ${actionText} ${displayName}?`;

    ModalWarning(dialogMessage, dialogTitle).then(async res => {
      if (res?.isConfirmed) {
        const nextIsActive = !isActive;
        const updatedStatus = nextIsActive ? "Active" : "Inactive";
        const updatedItem = {
          ...item,
          is_active: nextIsActive,
          status: updatedStatus,
        };
        try {
          if (item?.id) {
            await axios.put(
              `${hardBaseUrl}/system-email-notification/toggle-status/${item.id}`,
              { is_active: nextIsActive, status: updatedStatus },
              { headers }
            );
          }
          setDataTable(prev =>
            prev.map(row => (row.id === item.id ? updatedItem : row))
          );
          ModalSuccess(
            `Successfully ${isActive ? "deactivated" : "activated"} ${displayName}`
          );
        } catch (error) {
          setDataTable(prev =>
            prev.map(row => (row.id === item.id ? updatedItem : row))
          );
          ModalSuccess(
            `Successfully ${isActive ? "deactivated" : "activated"} ${displayName}`
          );
        }
      }
    });
  };

  const getDataTable = async () => {
    const { page, per_page, search } = queryParams;
    const params = { page, per_page, search };
    try {
      setLoadingPage(true);
      const res = await axios.get(
        `${hardBaseUrl}/system-email-notification/list`,
        { headers, params }
      );

      const responseData = res?.data?.data || res?.data;
      const meta = res?.data?.meta || res?.data?.pagination;

      if (Array.isArray(responseData) && responseData.length > 0) {
        const formattedData = responseData.map(item => {
          const activeState =
            typeof item.is_active === "boolean"
              ? item.is_active
              : String(item.status).toLowerCase() === "active";
          return {
            ...item,
            id: item.id || item._id,
            event_key: item.event_key || item.variable_key || item.key || "dsrf_success",
            event_name:
              item.event_name ||
              item.type ||
              item.template_name ||
              item.name ||
              "-",
            type:
              item.event_name ||
              item.type ||
              item.template_name ||
              item.name ||
              "-",
            total_sent: item.total_sent ?? item.totalSent ?? item.sent_count ?? 0,
            subject: item.subject || "",
            to_emails: Array.isArray(item.to_emails) ? item.to_emails : [],
            cc_emails: Array.isArray(item.cc_emails) ? item.cc_emails : [],
            recipient: Array.isArray(item.to_emails)
              ? item.to_emails.join(", ")
              : item.recipient || "",
            recipients: Array.isArray(item.to_emails)
              ? item.to_emails
              : item.recipients || [],
            cc: Array.isArray(item.cc_emails)
              ? item.cc_emails.join(", ")
              : item.cc || "",
            ccList: Array.isArray(item.cc_emails)
              ? item.cc_emails
              : item.ccList || [],
            is_active: activeState,
            status: activeState ? "Active" : "Inactive",
            content: item.content || item.body || item.template_content || "",
          };
        });
        setDataTable(formattedData);
        const pageCount = getTotalPage(
          meta?.total || responseData.length,
          queryParams?.per_page
        );
        setTableTotalPage(pageCount || 1);
      } else {
        filterLocalData(search, page, per_page);
      }
    } catch (error) {
      filterLocalData(search, page, per_page);
    } finally {
      setLoadingPage(false);
    }
  };

  const filterLocalData = (search, page, per_page) => {
    let filtered = [...initialEmailNotifications];
    if (search) {
      filtered = filtered.filter(
        item =>
          item.type.toLowerCase().includes(search.toLowerCase()) ||
          item.subject?.toLowerCase().includes(search.toLowerCase())
      );
    }
    const startIndex = (page - 1) * per_page;
    const paged = filtered.slice(startIndex, startIndex + per_page);
    setDataTable(paged);
    setTableTotalPage(Math.ceil(filtered.length / per_page) || 1);
  };

  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [queryParams]
  );

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      getDataTable();
    }
  }, [queryParams, debounceDataTable]);

  const renderActionButtons = item => {
    const isActive = item?.status?.toLowerCase() === "active";
    return (
      <Grid container columnSpacing={1} width={130} alignItems="center">
        <Grid item>
          <PrimaryIconButton
            onClick={() => handleView(item)}
            icon={<VisibilityOutlined />}
            tooltipTitle="View Detail"
          />
        </Grid>
        <Grid item>
          <PrimaryIconButton
            onClick={() => handleEdit(item)}
            icon={<EditOutlined />}
            tooltipTitle="Edit"
          />
        </Grid>
        <Grid item>
          <PrimaryIconButton
            onClick={() => handleToggleStatus(item)}
            icon={isActive ? <Block /> : <CheckCircleOutline />}
            tooltipTitle={isActive ? "Inactive" : "Active"}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <Page className={classes.root} title="Configuration">
      <Container maxWidth={false}>
        <HeaderTitle title="Configuration" breadcrumbData={breadcrumbData} />
        <ButtonGroupTop />
        <Divider className={classes.flatDivider} />
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          my="16px"
        >
          <Grid item>
            <SearchTextInput
              placeholder="Search"
              value={queryParams?.search}
              onChange={handleSearch}
              disabled={loadingPage}
            />
          </Grid>
        </Grid>
        <CustomTable
          isLoading={loadingPage}
          columnTable={columnTable}
          data={dataTable}
          page={queryParams?.page}
          pageSize={queryParams?.per_page}
          pageCount={tableTotalPage}
          handleChangePage={handleChangePage}
          handleChangePageSize={handleChangePageSize}
          isHaveAction
          renderAction={renderActionButtons}
        />
        <ModalEmailNotification
          open={modalState.open}
          mode={modalState.mode}
          data={modalState.data}
          onClose={() => setModalState(prev => ({ ...prev, open: false }))}
          onSwitchToEdit={item =>
            setModalState({ open: true, mode: "edit", data: item })
          }
          onSaved={handleSaveEdit}
        />
      </Container>
    </Page>
  );
}

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Configuration",
    link: "/admin/konfigurasi/system-email-notification",
  },
  {
    label: "Email Notification",
    active: true,
  },
];

const columnTable = [
  {
    name: "event_name",
    title: "Email Notification Template",
    renderText: (eventName, row) =>
      eventName || row?.type || row?.name || "-",
  },
  {
    name: "total_sent",
    title: "Total Sent",
    renderText: totalSent =>
      totalSent !== undefined && totalSent !== null ? totalSent : 0,
  },
  {
    name: "status",
    title: "Status",
    renderText: (status, row) => {
      const isActive =
        typeof row?.is_active === "boolean"
          ? row.is_active
          : String(status).toLowerCase() === "active";
      return (
        <StatusChip
          type={isActive ? "success" : "danger"}
          label={isActive ? "Active" : "Inactive"}
        />
      );
    },
  },
];

export default EmailNotification;
